import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Delete, LogOut, Loader2 } from 'lucide-react';

import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

// Covers the whole app while a till is locked. The session underneath stays
// alive, so the cashier is back to work in four taps.
export const ShiftLockOverlay = () => {
  const { user, unlockShift, logout } = useAuth();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (value) => {
    setBusy(true);
    setError('');
    const result = await unlockShift(value);
    if (!result.ok) {
      setError(result.message || 'Wrong PIN');
      setPin('');
    }
    setBusy(false);
  };

  const press = (digit) => {
    if (busy) return;
    const next = (pin + digit).slice(0, 4);
    setPin(next);
    if (next.length === 4) submit(next);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) press(e.key);
      if (e.key === 'Backspace') setPin((p) => p.slice(0, -1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, busy]);

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-6" data-testid="shift-lock-overlay">
      <div className="w-full max-w-xs text-center">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center mb-4">
          <Lock className="h-6 w-6" />
        </div>
        <p className="text-slate-300 text-sm">Till locked</p>
        <h2 className="text-xl font-semibold text-white mt-0.5" data-testid="shift-lock-user">{user?.name || user?.email}</h2>
        <p className="text-slate-400 text-xs mt-1">Enter your 4-digit PIN to carry on</p>

        <div className="flex justify-center gap-3 my-6" data-testid="shift-lock-dots">
          {[0, 1, 2, 3].map((slot) => (
            <span
              key={slot}
              className={`h-3.5 w-3.5 rounded-full transition-colors ${pin.length > slot ? 'bg-emerald-400' : 'bg-slate-700'}`}
            />
          ))}
        </div>

        {error && <p className="text-rose-400 text-sm mb-4" data-testid="shift-lock-error">{error}</p>}
        {busy && <Loader2 className="h-4 w-4 animate-spin text-slate-400 mx-auto mb-4" />}

        <div className="grid grid-cols-3 gap-2.5">
          {KEYS.map((digit) => (
            <button
              key={digit} type="button" onClick={() => press(digit)}
              className="h-14 rounded-xl bg-slate-800/80 text-white text-xl font-medium hover:bg-slate-700 active:scale-95 transition-all"
              data-testid={`shift-lock-key-${digit}`}
            >
              {digit}
            </button>
          ))}
          <span />
          <button
            type="button" onClick={() => press('0')}
            className="h-14 rounded-xl bg-slate-800/80 text-white text-xl font-medium hover:bg-slate-700 active:scale-95 transition-all"
            data-testid="shift-lock-key-0"
          >
            0
          </button>
          <button
            type="button" onClick={() => setPin((p) => p.slice(0, -1))}
            className="h-14 rounded-xl bg-slate-800/40 text-slate-300 flex items-center justify-center hover:bg-slate-700 transition-all"
            aria-label="Delete last digit" data-testid="shift-lock-backspace"
          >
            <Delete className="h-5 w-5" />
          </button>
        </div>

        <Button
          variant="ghost" onClick={async () => navigate(await logout())}
          className="mt-6 text-slate-400 hover:text-white gap-2"
          data-testid="shift-lock-switch-user"
        >
          <LogOut className="h-4 w-4" /> Switch user
        </Button>
      </div>
    </div>
  );
};
